"use client"

import * as React from "react";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "./toast";
import { useToast } from "../hooks/use-toast";

// Helper function to generate a contextual ID from toast title
function generateToastId(title?: React.ReactNode, variant?: string): string {
  if (!title) return "toast";
  
  // Convert title to string if it's a React node
  const titleText = typeof title === 'string' 
    ? title 
    : typeof title === 'object' && title !== null && 'props' in title && typeof (title as any).props?.children === 'string'
    ? (title as any).props.children
    : String(title);
  
  // Convert to kebab-case and sanitize
  const sanitized = titleText
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50); // Limit length
  
  const variantSuffix = variant && variant !== 'default' ? `-${variant}` : '';
  return `toast-${sanitized}${variantSuffix}`;
}

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, snaplandId, ...props }) {
        // Use custom snaplandId if provided, otherwise auto-generate from title
        const baseId = snaplandId || generateToastId(title, variant ?? undefined);
        
        return (
          <Toast key={id} {...props} variant={variant} snaplandId={baseId}>
            <div className="flex flex-col gap-2 min-w-0 flex-1" data-snapland-id={`${baseId}-content`}>
              <div className="flex flex-col gap-1" data-snapland-id={`${baseId}-content-wrapper`}>
                {title && <ToastTitle snaplandId={`${baseId}-title`}>{title}</ToastTitle>}
                {description && (
                  <ToastDescription snaplandId={`${baseId}-description`}>{description}</ToastDescription>
                )}
              </div>
              {action && (
                <div className="flex justify-start" data-snapland-id={`${baseId}-action-container`}>
                  {action}
                </div>
              )}
            </div>
            <ToastClose snaplandId={`${baseId}-close-button`} />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
